"use client"

import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { userSelect } from "../../../redux/selectors/index"
import { notify } from "../../../utility/Helper1"
import { authActions } from "../../../redux/actions"
import PinAuth from "../../../utility/customComponents/PinAuth"
import WrapUpP2p from "../p2p/layouts/WrapUpP2p"
import { EyeIcon, EyeOffIcon, KeyIcon, ShieldCheckIcon, LockIcon, UnlockIcon, RefreshCwIcon } from "lucide-react"

const ChangePassword = () => {
  const dispatch = useDispatch()
  const userData = useSelector(userSelect)
  const [password, setPassword] = useState("")
  const [password2, setPassword2] = useState("")
  const [securityObj, setSecurityObj] = useState({})
  const [passwordUpdate, setPasswordUpdate] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)

  // Password strength checker
  const checkPasswordStrength = (pass) => {
    let strength = 0
    if (pass.length >= 8) strength += 1
    if (/[A-Z]/.test(pass)) strength += 1
    if (/[0-9]/.test(pass)) strength += 1
    if (/[^A-Za-z0-9]/.test(pass)) strength += 1
    return strength
  }

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value
    setPassword(newPassword)
    setPasswordStrength(checkPasswordStrength(newPassword))
  }

  const doPasswordChange = async (e) => {
    if (passwordUpdate) {
      return
    }
    e.preventDefault()

    if (password === "") {
      notify("Please input new password", 0)
      return false
    }

    if (password !== password2) {
      notify("Password mismatch. Enter valid passwords", 0)
      return false
    }

    const data = { password, ...(securityObj ?? {}) }

    setPasswordUpdate(true)

    dispatch(
      authActions.onLogin("account/change_password", data, (response) => {
        if (response.code == 1) {
          setSecurityObj({})
          setPassword("")
          setPassword2("")
          setPasswordStrength(0)
        }
        notify(response.msg, response.code)
        setPasswordUpdate(false)
      }),
    )
  }

  return (
    <WrapUpP2p pageTitle={"Change Password"}>
      <div className="max-w-4xl mx-auto">
        <div className="bg-black rounded-3xl shadow-xl overflow-hidden border border-purple-500 relative">
          {/* Background gradient effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20 pointer-events-none"></div>

          {/* Glow effects */}
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-purple-500 rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>

          <div className="relative z-10">
            <div className="px-8 py-6 border-b border-gray-800">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 mr-4">
                  <KeyIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">Secure Your Account</h3>
                  <p className="text-gray-400">Create a strong password to protect your information</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              <form onSubmit={doPasswordChange} className="space-y-8">
                <div className="space-y-6">
                  {/* New Password Field */}
                  <div className="space-y-2">
                    <label className="flex items-center text-gray-300 font-medium">
                      <LockIcon className="h-4 w-4 mr-2 text-purple-400" />
                      New Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl blur group-hover:blur-md transition-all duration-300"></div>
                      <div className="relative bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={handlePasswordChange}
                          className="w-full px-4 py-4 bg-transparent text-white focus:outline-none"
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Password strength indicator */}
                    {password && (
                      <div className="mt-2">
                        <div className="flex gap-1 h-1">
                          <div className={`w-1/4 rounded-full ${passwordStrength >= 1 ? 'bg-red-500' : 'bg-gray-700'}`}></div>
                          <div className={`w-1/4 rounded-full ${passwordStrength >= 2 ? 'bg-yellow-500' : 'bg-gray-700'}`}></div>
                          <div className={`w-1/4 rounded-full ${passwordStrength >= 3 ? 'bg-blue-500' : 'bg-gray-700'}`}></div>
                          <div className={`w-1/4 rounded-full ${passwordStrength >= 4 ? 'bg-green-500' : 'bg-gray-700'}`}></div>
                        </div>
                        <p className="text-xs mt-1 text-gray-400">
                          {passwordStrength === 0 && "Very weak"}
                          {passwordStrength === 1 && "Weak"}
                          {passwordStrength === 2 && "Medium"}
                          {passwordStrength === 3 && "Strong"}
                          {passwordStrength === 4 && "Very strong"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="space-y-2">
                    <label className="flex items-center text-gray-300 font-medium">
                      <UnlockIcon className="h-4 w-4 mr-2 text-blue-400" />
                      Confirm Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur group-hover:blur-md transition-all duration-300"></div>
                      <div className="relative bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={password2}
                          onChange={(e) => setPassword2(e.target.value)}
                          className="w-full px-4 py-4 bg-transparent text-white focus:outline-none"
                          placeholder="Confirm your password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Password match indicator */}
                    {password && password2 && (
                      <div className="flex items-center mt-1 text-xs">
                        <div className={`w-2 h-2 rounded-full mr-2 ${password === password2 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className={password === password2 ? 'text-green-400' : 'text-red-400'}>
                          {password === password2 ? 'Passwords match' : 'Passwords do not match'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>


                <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />


                {/* Submit Button */}
                <div className="pt-4">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-xl blur-md group-hover:blur-xl transition-all duration-300 opacity-70"></div>
                    <button
                      type="submit"
                      disabled={passwordUpdate}
                      className="relative w-full py-4 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 hover:opacity-90 transition-all duration-300 flex items-center justify-center"
                    >
                      {passwordUpdate ? (
                        <div className="flex items-center justify-center">
                          <RefreshCwIcon className="animate-spin h-5 w-5 mr-2" />
                          Updating Password...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <KeyIcon className="h-5 w-5 mr-2" />
                          Update Password
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Security tips */}
        <div className="mt-6 bg-gray-900/70 p-4 rounded-xl border border-gray-800 text-gray-300">
          <h5 className="font-medium text-gray-200 mb-2 flex items-center">
            <ShieldCheckIcon className="h-4 w-4 text-purple-400 mr-2" />
            Password Security Tips
          </h5>
          <ul className="text-sm space-y-1 text-gray-400">
            <li>• Use at least 8 characters with uppercase, numbers and symbols</li>
            <li>• Avoid using personal information or common words</li>
            <li>• Use unique passwords for different accounts</li>
          </ul>
        </div>
      </div>
    </WrapUpP2p>
  )
}

export default ChangePassword